"use client"
import { useState } from "react"

const RegistrationPage = ({ userData, onNext, onBack, onUserDataUpdate }) => {
  const [formData, setFormData] = useState({
    name: userData.name || "",
    company: userData.company || "",
    email: userData.email || "",
    role: userData.role || "",
    companySize: userData.companySize || "",
  })
  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = "Full name is required"
    }
    if (!formData.company.trim()) {
      newErrors.company = "Company name is required"
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }
    if (!formData.role.trim()) {
      newErrors.role = "Role is required"
    }
    if (!formData.companySize) {
      newErrors.companySize = "Company size is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validateForm()) {
      onUserDataUpdate(formData)
      onNext()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/subtle-white-feathers.png')] opacity-20 z-0"></div>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 animate-fade-in">GTM Assessment</h1>
            <div className="flex items-center space-x-4 md:space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center text-sm font-semibold animate-pulse">
                  1
                </div>
                <span className="text-sm font-medium text-cyan-600 hidden sm:block">Registration</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <span className="text-sm font-medium text-gray-500 hidden sm:block">Assessment</span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <span className="text-sm font-medium text-gray-500 hidden sm:block">Results</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="card bg-white rounded-2xl shadow-lg p-8 animate-fade-in-up">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Let's Get Started</h2>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              Provide your details to personalize your GTM assessment and unlock tailored insights.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`input-field w-full rounded-md border ${errors.name ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-all duration-200`}
                placeholder="Enter your full name"
                aria-invalid={errors.name ? "true" : "false"}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-red-500 text-sm mt-1 animate-fade-in">{errors.name}</p>
              )}
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className={`input-field w-full rounded-md border ${errors.company ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-all duration-200`}
                placeholder="Enter your company name"
                aria-invalid={errors.company ? "true" : "false"}
                aria-describedby={errors.company ? "company-error" : undefined}
              />
              {errors.company && (
                <p id="company-error" className="text-red-500 text-sm mt-1 animate-fade-in">{errors.company}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`input-field w-full rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-all duration-200`}
                placeholder="Enter your email address"
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <p id="email-error" className="text-red-500 text-sm mt-1 animate-fade-in">{errors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                Your Role *
              </label>
              <input
                type="text"
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`input-field w-full rounded-md border ${errors.role ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-all duration-200`}
                placeholder="e.g., CEO, Marketing Director, Product Manager"
                aria-invalid={errors.role ? "true" : "false"}
                aria-describedby={errors.role ? "role-error" : undefined}
              />
              {errors.role && (
                <p id="role-error" className="text-red-500 text-sm mt-1 animate-fade-in">{errors.role}</p>
              )}
            </div>
            <div>
              <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-2">
                Company Size *
              </label>
              <select
                id="companySize"
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                className={`input-field w-full rounded-md border ${errors.companySize ? "border-red-500" : "border-gray-300"} focus:ring-2 focus:ring-cyan-600 focus:border-transparent transition-all duration-200`}
                aria-invalid={errors.companySize ? "true" : "false"}
                aria-describedby={errors.companySize ? "companySize-error" : undefined}
              >
                <option value="">Select company size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-1000">201-1000 employees</option>
                <option value="1000+">1000+ employees</option>
              </select>
              {errors.companySize && (
                <p id="companySize-error" className="text-red-500 text-sm mt-1 animate-fade-in">{errors.companySize}</p>
              )}
            </div>
            <div className="flex justify-between pt-6">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200"
              >
                Back
              </button>
              <button
                type="submit"
                className="btn-primary px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
              >
                Continue to Assessment
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}

export default RegistrationPage